import { CvdChartData, CvdChartDataValue, CvdBandDataValue } from '@covance/ng-cvd3';
import { LabTestResult, TestResult } from '../lab-test-results';
import { chain, isNumber, max, min, maxBy, Dictionary, forEachRight, find } from 'lodash';
import { scaleLinear } from 'd3';
import { Injectable } from '@angular/core';
import { LabResultsSortInfo } from '../lab-test-results';
import { getFlaggedClassName } from '../charts/chart-helper';

export interface LabTestsChartXAxisTick {
    visitId: number;
    visitColumnKey: string;
    visitTitle: string;
}

export interface LastVisitInfo {
    value: number;
    colorClassName: string;
}

export interface LabTestsChartYAxisTick {
    value: number;
    testGroupName: string;
    yTickText: string;
    lastVisitInfo: LastVisitInfo;
}

export interface LabTestsChartDataValue extends CvdChartDataValue {
    testNumbericResult: number;
    testUnit: string;
    testMin: number;
    testMax: number;
    shape: string;
    labTestResult: LabTestResult;
    colorClassName: string;
}

export interface LabTestsChartData extends CvdChartData<CvdChartDataValue> {
    values: LabTestsChartDataValue[];
}

export interface LabTestsChartDataResult {
    chartData: LabTestsChartData[];
    chartBandData: CvdChartData<CvdBandDataValue>[];
    xAxisTicks: LabTestsChartXAxisTick[];
    xAxisTickValues: number[];
    yAxisTicks: LabTestsChartYAxisTick[];
    yAxisTickValues: number[];
    yDomain: number[];
}

interface VisitInfo {
    visitId: number;
    visitColumnKey: string;
    visitDescription: string;
    kitTypeDescription: string;
}

@Injectable()
export class LabTestsChartDataGenerator {
    constructor(private labTestsSortingInfo: LabResultsSortInfo) { }

    public generateData(labTestResults: LabTestResult[]): LabTestsChartDataResult {
        const generator = new LabTestsChartDataGeneratorInternal(this.labTestsSortingInfo);
        return generator.generateData(labTestResults);
    }
}

class LabTestsChartDataGeneratorInternal {
    private readonly heightChartLine = 10;
    private readonly paddingChartLine = 0.5;

    private indexLine = 0;
    private chartData: LabTestsChartData[] = null;
    private readonly bandData: CvdChartData<CvdBandDataValue>[] = [];
    private visitData: VisitInfo[] = [];
    private xTicks: LabTestsChartXAxisTick[] = [];
    private readonly yTicks: LabTestsChartYAxisTick[] = [];

    constructor(private labResultsSortInfo: LabResultsSortInfo) { }

    public generateData(labTestResults: LabTestResult[]): LabTestsChartDataResult {
        if (labTestResults) {
            this.chartData = [];
            chain(labTestResults)
                .groupBy(x => x.testGroup)
                .orderBy((x: LabTestResult[]) => this.labResultsSortInfo.subjectLevel.testGroupLevel(x[0]), 'desc')
                .forEach((byTestGroup: LabTestResult[]) => {
                    const testGroup = byTestGroup[0].testGroup;

                    chain(byTestGroup)
                        .groupBy(x => x.zcTestCode)
                        .orderBy((x: LabTestResult[]) => this.labResultsSortInfo.subjectLevel.testLevel(x[0]), 'desc')
                        .forEach((byZcTestCode: LabTestResult[]) => {
                            const firstLabTestResult = find(byZcTestCode, labTestResult => labTestResult.currentResult && isNumber(labTestResult.currentResult.numericResult));

                            if (firstLabTestResult) {
                                const chartDataValues: LabTestsChartDataValue[] = [];
                                const bandValues: CvdBandDataValue[] = [];

                                this.fillValues(byZcTestCode, chartDataValues, bandValues);
                                this.scaleYValues(chartDataValues, bandValues, firstLabTestResult.currentResult);
                                this.fillChartData(
                                    chartDataValues,
                                    bandValues,
                                    firstLabTestResult.uniqLabTestIdKey,
                                    firstLabTestResult.test,
                                    testGroup);

                                ++this.indexLine;
                            }
                        })
                        .value();

                })
                .value();
            
            
            this.xTicks = chain(this.visitData)
                .groupBy(x => x.visitId)
                .map((visitData: VisitInfo[], visitId: number) => {
                    const visitWithDescription = visitData.find(v => !!v.visitDescription);
                    const visitTitle = visitWithDescription ? 
                        visitWithDescription.visitDescription 
                        : visitData[0].kitTypeDescription;
                    return {
                        visitId: visitId,
                        visitColumnKey: visitData[0].visitColumnKey,
                        visitTitle: visitTitle
                    };
                })
                .sortBy(x => x.visitColumnKey)     
                .value();

            this.mapVisitIdsToNumbericIndex();
            this.distinctYTicksTestGroupNames();
        }
        const xAxisTickValues = this.xTicks.map(x => x.visitId);
        const yAxisTickValues = this.yTicks.map(x => x.value);
        const yDomain = [0, this.bandData.length * this.heightChartLine];

        return  {
            chartData: this.chartData,
            chartBandData: this.bandData,
            xAxisTicks: this.xTicks,
            xAxisTickValues: xAxisTickValues,
            yAxisTicks: this.yTicks,
            yAxisTickValues: yAxisTickValues,
            yDomain: yDomain
        };
    }

    private distinctYTicksTestGroupNames() {
        let prev: string = null;

        // use right, since yTicks are rendered from the bottom to the top
        forEachRight(this.yTicks, yTick => {
            const testGroupName = yTick.testGroupName;
            if (prev === yTick.testGroupName) {
                yTick.testGroupName = '';
            }
            prev = testGroupName;
        });
    }

    // maps visitId to numeric index: visitId1 -> 1, visitId2 -> 2, ..., visitIdN -> N
    // for correct axises rendering.
    private mapVisitIdsToNumbericIndex() {
        const mappingVisitIdsToIndexes: Dictionary<number> = {};

        this.xTicks.forEach((xTick, index) => {
            mappingVisitIdsToIndexes[xTick.visitId] = index;
            xTick.visitId = index;
        });

        this.chartData.forEach(dataItem => {
            dataItem.values.forEach(valueItem => {
                valueItem.x = mappingVisitIdsToIndexes[valueItem.x];
            });
        });

        this.bandData.forEach(bandItem => {
            bandItem.values.forEach(valueItem => {
                valueItem.x = mappingVisitIdsToIndexes[valueItem.x];
            });
        });
    }

    private fillValues(groupedLabTestResults: LabTestResult[], chartDataValues: LabTestsChartDataValue[], bandValues: CvdBandDataValue[]) {
        chain(groupedLabTestResults)
            .filter(labTestResult => !labTestResult.isCancelled && labTestResult.currentResult && isNumber(labTestResult.currentResult.numericResult))
            .sortBy(labTestResult => labTestResult.visitColumnKey) // sort by x
            .forEach(labTestResult => {
                const testResult = labTestResult.currentResult;

                if (testResult) {
                    chartDataValues.push({
                        x: labTestResult.accessionId,
                        y: testResult.numericResult,
                        testNumbericResult: testResult.numericResult,
                        testUnit: testResult.unit,
                        testMin: testResult.min,
                        testMax: testResult.max,
                        labTestResult: labTestResult,
                        rows: [],
                        series: 0,
                        shape: 'square',
                        colorClassName: getFlaggedClassName(labTestResult)
                    });

                    this.fillBandValues(labTestResult, testResult, bandValues);

                    this.visitData.push({
                        visitId: labTestResult.accessionId,
                        visitColumnKey: labTestResult.visitColumnKey,
                        visitDescription: labTestResult.visitDescription,
                        kitTypeDescription: labTestResult.visitTitle
                    });
                }
            })
            .value();
    }

    private fillBandValues(labTestResult: LabTestResult, testResult: TestResult, bandValues: CvdBandDataValue[]) {
        if (bandValues.length === 1 // Suppose min and max are permanent for all visits.
        || testResult.min === null
        || testResult.max === null) {
            return;
        }

        bandValues.push({
            x: labTestResult.accessionId,
            y1: testResult.min,
            y2: testResult.max,
            rows: [],
            series: 0,
            y: 0
        });
    }

    private scaleYValues(chartDataValues: LabTestsChartDataValue[], bandValues: CvdBandDataValue[], testResult: TestResult) {
        const yValues = [...chartDataValues.map(x => x.y as number), testResult.min, testResult.max];
        const maxY = max(yValues);
        const minY = min(yValues);

        const scaleYAxis = scaleLinear()
            .domain([minY, maxY])
            .range([this.paddingChartLine, this.heightChartLine - this.paddingChartLine]);

        bandValues.forEach(c => {
            c.y1 = scaleYAxis(c.y1) + (this.heightChartLine * this.indexLine);
            c.y2 = scaleYAxis(c.y2) + (this.heightChartLine * this.indexLine);
        });

        chartDataValues.forEach(v => {
            v.y = scaleYAxis(v.y as number) + (this.heightChartLine * this.indexLine);
        });
    }

    private fillChartData(
        chartDataValues: LabTestsChartDataValue[],
        bandValues: CvdBandDataValue[],
        uniqLabTestKey: string,
        testName: string,
        testGroupName: string
    ) {
        this.bandData.push({
            key: uniqLabTestKey,
            values: bandValues,
            rows: []
        });

        this.chartData.push({
            key: uniqLabTestKey,
            rows: [],
            values: chartDataValues
        });

        const lastVisitValue = maxBy(chartDataValues, value => value.labTestResult.visitColumnKey);

        const lastVisitInfo: LastVisitInfo = lastVisitValue
            ? {
                value: lastVisitValue.testNumbericResult,
                colorClassName: lastVisitValue.colorClassName
            }
            : null;

        this.yTicks.push(
            {
                value: (this.indexLine + 0.5) * this.heightChartLine,
                testGroupName: testGroupName,
                yTickText: testName,
                lastVisitInfo: lastVisitInfo
            }
        );
    }
}